import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Save, Globe, Shield, Smartphone, Mail, AlertTriangle } from "lucide-react";
import Swal from 'sweetalert2';

export default function Settings() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("General");
  
  const [formData, setFormData] = useState({
    appName: "Social Bazar",
    supportEmail: "support@socialbazar.co",
    contactPhone: "+91 9876543210",
    maintenanceMode: false,
    allowRegistrations: true,
    maxBgRemovalsFree: 10,
    googleAnalyticsId: "G-XXXXXXXXXX",
    fcmServerKey: "AAAAxxxxxxxxxxx..."
  });

  const handleSave = () => {
    Swal.fire({
      icon: 'success',
      title: 'Settings Saved',
      text: 'Your application settings have been updated.',
      confirmButtonColor: '#2563eb'
    });
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative w-full lg:w-[calc(100%-16rem)]">
        <Header 
          onMenuClick={() => setIsSidebarOpen(true)} 
          title="System Settings" 
          subtitle="Configure global application settings and keys."
        />

        <div className="p-4 sm:p-8 flex-1 w-full">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              
              
            </div>
            <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm px-6 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2">
              <Save size={18} /> Save Changes
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            
            {/* Settings Sidebar */}
            <div className="w-full md:w-64 shrink-0">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col p-2">
                {[
                  { name: 'General', icon: Globe },
                  { name: 'App Features', icon: Smartphone },
                  { name: 'API & Keys', icon: Shield },
                  { name: 'Email Settings', icon: Mail },
                ].map((tab) => (
                  <button
                    key={tab.name}
                    onClick={() => setActiveTab(tab.name)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors w-full text-left ${activeTab === tab.name ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    <tab.icon size={18} className={activeTab === tab.name ? "text-blue-600" : "text-slate-400"} />
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Content */}
            <div className="flex-1">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 sm:p-8">
                
                {activeTab === 'General' && (
                  <div className="space-y-6 animate-in fade-in duration-200">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">App Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Application Name</label>
                          <input type="text" value={formData.appName} onChange={e => setFormData({...formData, appName: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Support Email</label>
                          <input type="email" value={formData.supportEmail} onChange={e => setFormData({...formData, supportEmail: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Phone</label>
                          <input type="text" value={formData.contactPhone} onChange={e => setFormData({...formData, contactPhone: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">System Status</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl">
                          <div>
                            <p className="font-semibold text-slate-800 dark:text-slate-100">Maintenance Mode</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Show under maintenance page to all users.</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={formData.maintenanceMode} onChange={e => setFormData({...formData, maintenanceMode: e.target.checked})} className="sr-only peer" />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-slate-800 after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'App Features' && (
                  <div className="space-y-6 animate-in fade-in duration-200">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">Features Toggle</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl">
                          <div>
                            <p className="font-semibold text-slate-800 dark:text-slate-100">Allow New Registrations</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Enable or disable new user signups.</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={formData.allowRegistrations} onChange={e => setFormData({...formData, allowRegistrations: e.target.checked})} className="sr-only peer" />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-slate-800 after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div>
                       <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">AI Limits</h3>
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Max BG Removals (Free Tier)</label>
                          <input type="number" value={formData.maxBgRemovalsFree} onChange={e => setFormData({...formData, maxBgRemovalsFree: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'API & Keys' && (
                  <div className="space-y-6 animate-in fade-in duration-200">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                      <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
                      <div>
                        <h4 className="font-bold text-amber-800 text-sm">Caution</h4>
                        <p className="text-xs text-amber-700 mt-1">Changing these keys might break app functionality (e.g., Push Notifications, Analytics). Only modify if you know what you are doing.</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">Firebase Cloud Messaging</h3>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">FCM Server Key</label>
                        <input type="password" value={formData.fcmServerKey} onChange={e => setFormData({...formData, fcmServerKey: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm font-mono text-slate-600" />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">Google Analytics</h3>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Measurement ID</label>
                        <input type="text" value={formData.googleAnalyticsId} onChange={e => setFormData({...formData, googleAnalyticsId: e.target.value})} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm font-mono text-slate-600" />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'Email Settings' && (
                   <div className="space-y-6 animate-in fade-in duration-200">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Configure SMTP settings here in the future.</p>
                   </div>
                )}

              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
